// policy.js — Policy page sub-renderers + interaction handlers.
//
// Owns:
//   - applyPolicyListFilter   filter rules by text + decision chip
//   - renderPolicyFilterBar   chip bar above the rule list
//   - renderPolicyRuleRow     dense one-line rule display
//   - renderPolicyRuleCard    older card-style display (legacy paths)
//   - renderRuleSentence      sentence-shaped rule description
//   - renderPolicyRecentFirings  24h aggregate of policy.* events
//   - renderDiscoveredTools   tool-usage aggregation
//   - wirePolicyRuleDragDrop  bind HTML5 DnD handlers to rule rows
//   - onRuleDrag*             5 drag/drop handlers
//
// Why this is a sibling module rather than appended to
// pages/policy.js: pages/policy.js is the entry-point that owns
// the section's lifecycle + top-level render. These are the
// implementation details. Keeping them separate matches how
// fetchers.js / format.js / etc. work — focused modules with one
// concern.

import { state } from "./state.js";
import { policyState, policyListFilter } from "./page-state.js";
import { escapeHTML, ago } from "./util.js";
import { api, apiFetch } from "./api.js";
import { decryptHint } from "./crypto.js";
import { showToast, isCloudMode } from "./ui.js";

// Shared list of starter rule names — auto-seeded on first tenant
// load. Used for the "Default" chip annotation in the rule list.
const STARTER_RULES = new Set([
  "block-writes-to-secrets",
  "block-writes-to-env-files",
  "ask-before-destructive-bash",
  "ask-before-lockfile-edits",
  "sandbox-agents-stay-off-main",
]);

// applyPolicyListFilter filters the rule list by the user's active
// search text and decision chip selection.
export function applyPolicyListFilter(rules) {
  const q = policyListFilter.text.toLowerCase().trim();
  return rules.filter((r) => {
    if (policyListFilter.decision !== "all") {
      if (policyListFilter.decision === "audit") {
        if (r.mode !== "audit") return false;
      } else if (policyListFilter.decision === "default") {
        if (!STARTER_RULES.has(r.name || "")) return false;
      } else {
        if ((r.decision || "forbid") !== policyListFilter.decision)
          return false;
      }
    }
    if (!q) return true;
    const c = r.constraint || {};
    const hay = [
      r.name || "",
      r.reason || "",
      (c.action || []).join(" "),
      (c.resource && c.resource.path) || "",
      ((c.principal && c.principal.role) || []).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.indexOf(q) >= 0;
  });
}

// renderPolicyFilterBar emits the chip bar above the rule list with
// per-chip rule counts and a search input.
export function renderPolicyFilterBar(allRules, filteredRules) {
  const chips = [
    { value: "all", label: "All", count: allRules.length },
    {
      value: "forbid",
      label: "Blocks",
      count: allRules.filter(
        (r) =>
          (r.decision || "forbid") === "forbid" && r.mode !== "audit",
      ).length,
    },
    {
      value: "require_confirm",
      label: "Asks first",
      count: allRules.filter((r) => r.decision === "require_confirm")
        .length,
    },
    {
      value: "audit",
      label: "Audit only",
      count: allRules.filter((r) => r.mode === "audit").length,
    },
    {
      value: "default",
      label: "Default",
      count: allRules.filter((r) => STARTER_RULES.has(r.name || ""))
        .length,
    },
  ];
  let h =
    '<div style="display: flex; gap: 8px; align-items: center; margin-bottom: 14px; flex-wrap: wrap;">';
  h +=
    '<input type="text" id="policy-list-filter" placeholder="Search rules (name, reason, path)…" value="' +
    escapeHTML(policyListFilter.text) +
    '" style="flex: 1; min-width: 200px; padding: 7px 10px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--bg-panel-solid); color: var(--text); font-size: 13px;" />';
  for (const c of chips) {
    const active = policyListFilter.decision === c.value;
    h +=
      '<button type="button" data-policy-filter-decision="' +
      escapeHTML(c.value) +
      '" class="' +
      (active ? "btn btn-sm btn-primary" : "btn btn-sm") +
      '" style="font-size: 12px;">' +
      escapeHTML(c.label) +
      (c.count > 0
        ? ' <span style="opacity: 0.7; margin-left: 4px;">' +
          c.count +
          "</span>"
        : "") +
      "</button>";
  }
  h +=
    '<span style="font-size: 12px; color: var(--text-tertiary); margin-left: auto;">' +
    filteredRules.length +
    " of " +
    allRules.length +
    "</span>";
  h += "</div>";
  return h;
}

// renderRuleSentence builds the natural-language sentence describing
// a rule. Used by both the row and card layouts. Always returns
// HTML — callers interpolate it directly.
export function renderRuleSentence(r) {
  const decision = r.decision || "forbid";
  const c = r.constraint || {};
  const p = c.principal || {};
  // Who.
  let who = "Any agent";
  if (Array.isArray(p.role) && p.role.length) {
    who = p.role.map(escapeHTML).join(" or ") + " agents";
  } else if (Array.isArray(p.agent_id) && p.agent_id.length) {
    const ids = p.agent_id
      .slice(0, 2)
      .map((s) => "<code>" + escapeHTML(s) + "</code>")
      .join(" or ");
    who =
      "Agent " +
      ids +
      (p.agent_id.length > 2
        ? " (+" + (p.agent_id.length - 2) + " more)"
        : "");
  }
  // Verb.
  const plural = Array.isArray(p.role) && p.role.length > 0;
  let verb;
  if (decision === "require_confirm") verb = "must confirm before running";
  else if (r.mode === "audit") verb = "would be blocked from running";
  else verb = (plural ? "are" : "is") + " blocked from running";
  // Actions.
  let actions = "any tool";
  if (Array.isArray(c.action) && c.action.length) {
    actions = c.action
      .slice(0, 4)
      .map((a) => "<code>" + escapeHTML(a) + "</code>")
      .join(", ");
    if (c.action.length > 4)
      actions += " (+" + (c.action.length - 4) + " more)";
  }
  // Resource.
  const res = c.resource || {};
  const cmd = res.command_content || {};
  let where = "any file";
  if (res.path) {
    where = "<code>" + escapeHTML(res.path) + "</code>";
  } else if (cmd.starts_with) {
    where = "commands starting <code>" + escapeHTML(cmd.starts_with) + "</code>";
  } else if (cmd.contains) {
    where = "commands containing <code>" + escapeHTML(cmd.contains) + "</code>";
  }
  let line =
    "<strong>" +
    who +
    "</strong> " +
    verb +
    " <strong>" +
    actions +
    "</strong> on <strong>" +
    where +
    "</strong>.";
  // Secondary qualifiers — render as quiet chips after the sentence.
  const quals = [];
  if (res.branch) quals.push("branch " + res.branch);
  if (Array.isArray(res.tags) && res.tags.length)
    quals.push("tagged " + res.tags.join(" + "));
  if (res.path && (cmd.starts_with || cmd.contains))
    quals.push("command " + (cmd.starts_with || cmd.contains));
  if (Array.isArray(c.verb_class) && c.verb_class.length)
    quals.push(c.verb_class.map((v) => v.replace("_", "-")).join("/") + " actions only");
  if (Array.isArray(p.seniority) && p.seniority.length)
    quals.push(p.seniority.join("/") + " seniority");
  if (Array.isArray(p.domain) && p.domain.length)
    quals.push("domain " + p.domain.join("/"));
  if (decision === "require_confirm" && r.grace_seconds)
    quals.push(
      (r.grace_seconds % 60
        ? r.grace_seconds + " sec"
        : r.grace_seconds / 60 + " min") + " grace",
    );
  if (quals.length) {
    line +=
      ' <span style="color: var(--text-secondary); font-size: 12.5px;">(' +
      quals.map(escapeHTML).join(" · ") +
      ")</span>";
  }
  return line;
}

// renderPolicyRuleRow — dense, one-line rule display. Click the
// row body (the expand toggle button) to expand inline and show
// the reason + qualifiers.
export function renderPolicyRuleRow(r) {
  const ruleName = r.name || "";
  const decision = r.decision || "forbid";
  const isStarter = STARTER_RULES.has(ruleName);
  const expanded = policyListFilter.expanded.has(ruleName);
  const sentence = renderRuleSentence(r);

  const chipClass =
    decision === "forbid" ? "chip chip-danger" : "chip chip-warning";
  const chipLabel = decision === "forbid" ? "Blocks" : "Asks first";

  // Disabled rules render greyed-out (~50% opacity) with a Disabled
  // chip so the UI clearly communicates that the rule is staged but
  // not currently evaluated.
  const disabled = !!r.disabled;
  const rowOpacity = disabled ? "0.55" : "1";
  const toggleVerb = disabled ? "Enable" : "Disable";
  let h =
    '<div class="policy-rule-row" data-rule-name="' +
    escapeHTML(ruleName) +
    '" draggable="true" style="display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-bottom: 1px solid var(--border); background: var(--bg-panel-solid); cursor: grab; opacity: ' +
    rowOpacity +
    ';">';
  h +=
    '<span title="Drag to reorder" style="color: var(--text-tertiary); cursor: grab; user-select: none; font-size: 12px; flex-shrink: 0;">⋮⋮</span>';
  h +=
    '<button type="button" class="policy-rule-toggle" data-rule-name="' +
    escapeHTML(ruleName) +
    '" style="background: none; border: none; color: var(--text-tertiary); cursor: pointer; font-size: 10px; padding: 4px; flex-shrink: 0;" aria-label="Expand">' +
    (expanded ? "▾" : "▸") +
    "</button>";
  h +=
    '<strong style="font-size: 13.5px; color: var(--text-display); flex-shrink: 0; min-width: 100px; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="' +
    escapeHTML(ruleName) +
    '">' +
    escapeHTML(ruleName || "(unnamed)") +
    "</strong>";
  if (disabled) {
    h +=
      '<span class="chip" style="background: var(--bg-elevated); color: var(--text-tertiary); border: 1px solid var(--border); flex-shrink: 0;" title="This rule is currently inactive. Click Enable to activate.">Disabled</span>';
  }
  if (r.problem) {
    h +=
      '<span class="chip chip-danger" style="flex-shrink: 0;" title="' +
      escapeHTML(r.problem) +
      '">Not applied</span>';
  }
  h +=
    '<span class="' +
    chipClass +
    '" style="flex-shrink: 0;">' +
    chipLabel +
    "</span>";
  if (r.mode === "audit")
    h +=
      '<span class="chip chip-info" style="flex-shrink: 0;">Audit</span>';
  if (isStarter)
    h +=
      '<span class="chip" style="background: var(--bg-elevated); color: var(--text-secondary); border: 1px solid var(--border); flex-shrink: 0;">Default</span>';
  h +=
    '<div class="policy-rule-sentence" data-rule-name="' +
    escapeHTML(ruleName) +
    '" style="flex: 1; min-width: 0; font-size: 13px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">' +
    sentence +
    "</div>";
  h +=
    '<button class="btn btn-sm policy-rule-disable-btn" data-rule-name="' +
    escapeHTML(ruleName) +
    '" data-currently-disabled="' +
    (disabled ? "1" : "0") +
    '" title="' +
    toggleVerb +
    ' this rule" style="flex-shrink: 0;">' +
    toggleVerb +
    "</button>";
  h +=
    '<button class="btn btn-sm policy-rule-edit-btn" data-rule-name="' +
    escapeHTML(ruleName) +
    '" title="Edit this rule" style="flex-shrink: 0;">Edit</button>';
  h +=
    '<button class="btn btn-sm btn-danger-ghost policy-rule-delete-btn" data-rule-name="' +
    escapeHTML(ruleName) +
    '" title="Delete this rule" style="flex-shrink: 0;">Delete</button>';
  h += "</div>";

  if (expanded) {
    h +=
      '<div style="padding: 12px 16px 16px 50px; border-bottom: 1px solid var(--border); background: var(--bg-elevated); font-size: 13px; color: var(--text); line-height: 1.6;">';
    h += '<div style="margin-bottom: 6px;">' + sentence + "</div>";
    if (r.reason) {
      h +=
        '<div style="margin-top: 6px; color: var(--text-secondary); font-style: italic;">' +
        escapeHTML(r.reason) +
        "</div>";
    }
    h += "</div>";
  }

  return h;
}

// renderPolicyRuleCard — older card-style rule display. Legacy
// renderPolicyPage paths may still call this; the modern dense
// .policy-rule-row layout is preferred.
export function renderPolicyRuleCard(r) {
  const decision = r.decision || "forbid";
  const ruleName = r.name || "";
  const isStarter = STARTER_RULES.has(ruleName);
  const sentence = renderRuleSentence(r);

  const disabled = !!r.disabled;
  const cardOpacity = disabled ? "0.55" : "1";
  const toggleVerb = disabled ? "Enable" : "Disable";
  let h =
    '<div class="card card-hover policy-rule-card" data-rule-name="' +
    escapeHTML(ruleName) +
    '" draggable="true" style="margin-bottom: 12px; padding: 14px 16px; cursor: grab; opacity: ' +
    cardOpacity +
    ';">';

  // Top row: name + chips + actions.
  h +=
    '<div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">';
  h +=
    '<span title="Drag to reorder" style="color: var(--text-tertiary); cursor: grab; user-select: none; font-size: 12px;">⋮⋮</span>';
  h +=
    '<strong style="font-size: 14px; color: var(--text-display);">' +
    escapeHTML(ruleName || "(unnamed)") +
    "</strong>";
  if (disabled) {
    h +=
      '<span class="chip" title="This rule is currently inactive." style="background: var(--bg-elevated); color: var(--text-tertiary); border: 1px solid var(--border);">Disabled</span>';
  }
  const chipClass =
    decision === "forbid" ? "chip chip-danger" : "chip chip-warning";
  const chipLabel = decision === "forbid" ? "Blocks" : "Asks first";
  h +=
    '<span class="' +
    chipClass +
    '" title="' +
    escapeHTML(decision) +
    '">' +
    chipLabel +
    "</span>";
  if (r.mode === "audit") {
    h +=
      '<span class="chip chip-info" title="Audit-mode rule: logs a would-have-blocked event without denying.">Audit</span>';
  }
  if (isStarter) {
    h +=
      '<span class="chip" title="This is a starter rule installed by AgentCollision. Edit, customize, or delete it like any other rule." style="background: var(--bg-elevated); color: var(--text-secondary); border: 1px solid var(--border);">Default</span>';
  }
  h += '<div style="flex: 1;"></div>';
  h +=
    '<button class="btn btn-sm policy-rule-disable-btn" data-rule-name="' +
    escapeHTML(ruleName) +
    '" data-currently-disabled="' +
    (disabled ? "1" : "0") +
    '" title="' +
    toggleVerb +
    ' this rule">' +
    toggleVerb +
    "</button>";
  h +=
    '<button class="btn btn-sm policy-rule-edit-btn" data-rule-name="' +
    escapeHTML(ruleName) +
    '" title="Edit this rule">Edit</button>';
  h +=
    '<button class="btn btn-sm btn-danger-ghost policy-rule-delete-btn" data-rule-name="' +
    escapeHTML(ruleName) +
    '" title="Delete this rule">Delete</button>';
  h += "</div>";

  // Sentence line.
  h +=
    '<div style="font-size: 13.5px; line-height: 1.6; color: var(--text); margin-bottom: 6px;">' +
    sentence +
    "</div>";

  // Reason line.
  if (r.reason) {
    h +=
      '<div style="font-size: 12.5px; color: var(--text-secondary); font-style: italic;">' +
      escapeHTML(r.reason) +
      "</div>";
  }
  h += "</div>";
  return h;
}

// renderPolicyRecentFirings — 24h aggregate of policy.deny /
// policy.audit_deny / policy.confirm_required events bucketed by
// rule_name (decrypted from payload_hint). Falls back to
// (event_type via runtime) keyed buckets for events whose rule_name
// wasn't decryptable (pre-v0.26.1 daemons).
export async function renderPolicyRecentFirings() {
  const target = document.getElementById("policy-recent-firings");
  if (!target) return;
  let resp;
  try {
    resp = await api(
      "/v1/policy/recent-firings?window_hours=24&limit=500",
    );
  } catch (e) {
    target.innerHTML =
      '<div style="color: var(--text-muted); font-size: 12px; padding: 8px 0;">(could not load recent rule activity: ' +
      escapeHTML(e.message || String(e)) +
      ")</div>";
    return;
  }
  const events = (resp && resp.events) || [];
  if (events.length === 0) {
    target.innerHTML =
      '<div style="color: var(--text-tertiary); font-size: 13px; padding: 8px 0;">No rules fired in the last 24 hours. When a rule blocks or flags something a chat does, it shows up here.</div>';
    return;
  }
  // Decrypt payload hints when we can — extract rule_name from the
  // decrypted JSON.
  const buckets = new Map();
  for (const ev of events) {
    // Local daemon sends rule_name in plain text; the coordinator only has
    // the encrypted payload_hint.
    let ruleName = ev.rule_name || "";
    if (!ruleName && ev.payload_hint) {
      try {
        const dec = await decryptHint(ev.payload_hint);
        if (dec) {
          const parsed = JSON.parse(dec);
          if (parsed && parsed.rule_name) ruleName = parsed.rule_name;
        }
      } catch {
        // best-effort
      }
    }
    const aid = ev.agent_id || "";
    const runtime = aid.split("/")[0] || "unknown";
    const key =
      ruleName ||
      "(" + (ev.event_type || "unknown") + " via " + runtime + ")";
    let b = buckets.get(key);
    if (!b) {
      b = {
        rule_name: ruleName,
        key: key,
        runtime: runtime,
        total: 0,
        blocked: 0,
        audited: 0,
        confirmed: 0,
        last: "",
        samples: [],
      };
      buckets.set(key, b);
    }
    b.total++;
    if (ev.event_type === "policy.deny") b.blocked++;
    else if (
      ev.event_type === "policy.audit_deny" ||
      ev.event_type === "policy.audit_confirm"
    )
      b.audited++;
    else if (
      ev.event_type === "policy.confirm_required" ||
      ev.event_type === "policy.confirm_honored"
    )
      b.confirmed++;
    const t = ev.occurred_at || "";
    if (t > b.last) b.last = t;
    if (b.samples.length < 3) b.samples.push(ev);
  }
  const rows = Array.from(buckets.values()).sort(
    (a, b) => b.total - a.total,
  );

  let html =
    '<div style="overflow-x: auto;"><table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid var(--border); border-radius: var(--r-md); overflow: hidden;">';
  html +=
    '<thead><tr style="background: var(--bg-elevated); border-bottom: 1px solid var(--border);">';
  html +=
    '<th style="padding: 8px 12px; text-align: left; font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.05em;">Rule</th>';
  html +=
    '<th style="padding: 8px 12px; text-align: right; font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.05em;">Total</th>';
  html +=
    '<th style="padding: 8px 12px; text-align: right; font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.05em;">Blocked</th>';
  html +=
    '<th style="padding: 8px 12px; text-align: right; font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.05em;">Audited</th>';
  html +=
    '<th style="padding: 8px 12px; text-align: right; font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.05em;">Confirms</th>';
  html +=
    '<th style="padding: 8px 12px; text-align: right; font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.05em;">Last fired</th>';
  html += "</tr></thead><tbody>";
  for (const b of rows) {
    const label = b.rule_name
      ? "<strong>" + escapeHTML(b.rule_name) + "</strong>"
      : '<span style="color: var(--text-secondary);">' +
        escapeHTML(b.key) +
        "</span>";
    html +=
      '<tr style="border-bottom: 1px solid var(--border);">';
    html += '<td style="padding: 8px 12px;">' + label + "</td>";
    html +=
      '<td style="padding: 8px 12px; text-align: right; font-variant-numeric: tabular-nums;">' +
      b.total +
      "</td>";
    html +=
      '<td style="padding: 8px 12px; text-align: right; font-variant-numeric: tabular-nums; color: ' +
      (b.blocked > 0 ? "var(--danger)" : "var(--text-muted)") +
      ';">' +
      b.blocked +
      "</td>";
    html +=
      '<td style="padding: 8px 12px; text-align: right; font-variant-numeric: tabular-nums; color: ' +
      (b.audited > 0 ? "var(--warning)" : "var(--text-muted)") +
      ';">' +
      b.audited +
      "</td>";
    html +=
      '<td style="padding: 8px 12px; text-align: right; font-variant-numeric: tabular-nums; color: ' +
      (b.confirmed > 0 ? "var(--accent)" : "var(--text-muted)") +
      ';">' +
      b.confirmed +
      "</td>";
    html +=
      '<td style="padding: 8px 12px; text-align: right; color: var(--text-muted); font-size: 11px;">' +
      escapeHTML(ago(b.last) || "—") +
      "</td>";
    html += "</tr>";
  }
  html += "</tbody></table></div>";
  if (rows.some((r) => !r.rule_name)) {
    html +=
      '<div style="margin-top: 8px; font-size: 11px; color: var(--text-tertiary);">';
    html +=
      "Rows without a rule name came from an older AgentCollision version. Update AgentCollision to see rule names.";
    html += "</div>";
  }
  target.innerHTML = html;
}

// renderDiscoveredTools lists the tools chats actually call (Edit, Bash,
// Read, ...) so "+ Rule" can open the builder for one. Locally the daemon
// counts every hook call for 24h (/v1/tools/recent). The coordinator only
// has each active chat's latest tool (heartbeats), so the hosted page shows
// how many chats are using each tool now.
export async function renderDiscoveredTools() {
  const target = document.getElementById("policy-discovered-tools");
  if (!target) return;
  const cloud = isCloudMode();
  let rows;
  try {
    rows = cloud ? toolsFromHeartbeats(await api("/v1/teams/heartbeats")) : (await api("/v1/tools/recent")).tools || [];
  } catch (e) {
    target.innerHTML =
      '<div style="color: var(--text-muted); font-size: 12px; padding: 8px 0;">(could not load tool activity: ' +
      escapeHTML(e.message || String(e)) +
      ")</div>";
    return;
  }
  if (rows.length === 0) {
    target.innerHTML =
      '<div style="color: var(--text-muted); font-size: 13px; padding: 8px 0;">' +
      (cloud
        ? "No chat is calling a tool right now. Tools appear here while chats work."
        : "No tool calls in the last 24 hours. Tools appear here once a chat uses them.") +
      "</div>";
    return;
  }
  const th = (label, align) =>
    '<th style="padding: 8px 12px; text-align: ' + align + '; font-size: 11px; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.05em;">' + label + "</th>";
  let html =
    '<table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid var(--border, #ddd); border-radius: var(--r-md); overflow: hidden;">';
  html += '<thead><tr style="background: var(--bg-elevated, #f5f5f5); border-bottom: 1px solid var(--border);">';
  html += th("Tool", "left") + th("Used by", "left") + th(cloud ? "Chats now" : "Calls (24h)", "right") + th("Last seen", "right") + th("", "right");
  html += "</tr></thead><tbody>";
  for (const r of rows) {
    const runtimes = r.runtimes || [];
    html += '<tr style="border-bottom: 1px solid var(--border);">';
    html += '<td style="padding: 8px 12px;"><code>' + escapeHTML(r.tool) + "</code></td>";
    html +=
      '<td style="padding: 8px 12px;">' +
      runtimes.map((rt) => '<span class="type-badge" style="background: var(--bg-elevated); margin-right: 4px;">' + escapeHTML(rt) + "</span>").join("") +
      "</td>";
    html += '<td style="padding: 8px 12px; text-align: right; font-variant-numeric: tabular-nums;">' + (cloud ? r.chats : r.calls) + "</td>";
    html += '<td style="padding: 8px 12px; text-align: right; color: var(--text-muted); font-size: 11px;">' + escapeHTML(ago(r.last_seen) || "—") + "</td>";
    html +=
      '<td style="padding: 8px 12px; text-align: right;"><button class="btn btn-sm btn-primary discovered-tool-rule-btn" data-tool="' +
      escapeHTML(r.tool) +
      '" data-runtime="' +
      escapeHTML(runtimes.length === 1 ? runtimes[0] : "") +
      '" data-tooltip="Make a rule for this tool">+ Rule</button></td>';
    html += "</tr>";
  }
  html += "</tbody></table>";
  target.innerHTML = html;
}

// toolsFromHeartbeats groups the coordinator's live heartbeats by each
// chat's latest tool.
export function toolsFromHeartbeats(data) {
  const byTool = new Map();
  for (const h of (data && data.heartbeats) || []) {
    const tool = (h.last_tool || "").trim();
    if (!tool) continue;
    let b = byTool.get(tool);
    if (!b) {
      b = { tool, runtimes: new Set(), chats: 0, last_seen: "" };
      byTool.set(tool, b);
    }
    b.chats++;
    b.runtimes.add((h.agent_id || "").split("/")[0] || "unknown");
    if ((h.last_seen || "") > b.last_seen) b.last_seen = h.last_seen;
  }
  return Array.from(byTool.values())
    .map((b) => ({ ...b, runtimes: Array.from(b.runtimes).sort() }))
    .sort((x, y) => y.chats - x.chats || x.tool.localeCompare(y.tool));
}

// ---------- drag-to-reorder for rule cards ----------
//
// HTML5 drag-and-drop. Bound on every render because the cards are
// innerHTML'd fresh. The dragged card's data-rule-name is stashed
// in dataTransfer; on drop we compute the new order from the live
// DOM, optimistically re-render, and POST /v1/rules/draft/reorder.
//
// Module-local state: _dragSourceName tracks the in-flight drag.
// Reset on dragend.

let _dragSourceName = null;

// _onPolicyReorderRequest is the callback that pages/policy.js
// installs to ask for a re-render after a successful reorder. The
// drag handlers can't `import { renderPolicyPage }` directly
// because that would form a circular import — pages/policy.js
// imports from this module. Instead the page registers its render
// callback at module init time.
let _onPolicyReorderRequest = () => {};

export function setPolicyReorderCallback(fn) {
  _onPolicyReorderRequest = fn;
}

export function wirePolicyRuleDragDrop() {
  // Bind drag-drop to both old .policy-rule-card (legacy paths that
  // might still render cards somewhere) AND the new dense
  // .policy-rule-row layout.
  const cards = document.querySelectorAll(
    ".policy-rule-card, .policy-rule-row",
  );
  cards.forEach((card) => {
    card.addEventListener("dragstart", onRuleDragStart);
    card.addEventListener("dragover", onRuleDragOver);
    card.addEventListener("dragleave", onRuleDragLeave);
    card.addEventListener("drop", onRuleDrop);
    card.addEventListener("dragend", onRuleDragEnd);
  });
}

function onRuleDragStart(e) {
  const card = e.currentTarget;
  _dragSourceName = card.dataset.ruleName || "";
  card.style.opacity = "0.4";
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", _dragSourceName);
  }
}
function onRuleDragOver(e) {
  if (!_dragSourceName) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
  e.currentTarget.style.borderTop = "2px solid var(--accent)";
}
function onRuleDragLeave(e) {
  e.currentTarget.style.borderTop = "";
}
function onRuleDragEnd(e) {
  e.currentTarget.style.opacity = "";
  document
    .querySelectorAll(".policy-rule-card, .policy-rule-row")
    .forEach((c) => {
      c.style.borderTop = "";
    });
  _dragSourceName = null;
}
async function onRuleDrop(e) {
  e.preventDefault();
  const target = e.currentTarget;
  target.style.borderTop = "";
  if (!_dragSourceName) return;
  const targetName = target.dataset.ruleName || "";
  if (!targetName || targetName === _dragSourceName) return;
  // The full rule list, not the rendered rows: a filtered view shows a
  // subset, and both the daemon and the coordinator need every name.
  const names = (policyState.data?.rules || []).map((r) => r.name);
  const srcIdx = names.indexOf(_dragSourceName);
  const tgtIdx = names.indexOf(targetName);
  if (srcIdx < 0 || tgtIdx < 0) return;
  const [moved] = names.splice(srcIdx, 1);
  const insertAt = tgtIdx <= srcIdx ? tgtIdx : tgtIdx - 1;
  names.splice(insertAt, 0, moved);
  // Optimistic local reorder.
  const byName = new Map(
    (policyState.data?.rules || []).map((r) => [r.name, r]),
  );
  policyState.data.rules = names.map((n) => byName.get(n)).filter(Boolean);
  await _onPolicyReorderRequest();
  try {
    const resp = await apiFetch("/v1/rules/draft/reorder", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ names: names }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.message || data.error || "HTTP " + resp.status);
    }
    showToast("success", "Order saved.");
  } catch (err) {
    showToast(
      "error",
      "Reorder failed: " +
        (err.message || err) +
        ". Refreshing…",
    );
    policyState.data = null;
    await _onPolicyReorderRequest();
  }
}
